import { AccessLevel, AccessScopeType, AccessSourceType, Role } from '@/enums/auth'
import { t } from '@/libs/i18n'
import type { SelectOption, TagItem } from '@/types/Common'
import type { IAccessSource } from '@/types/User'

/**
 * §8.2/§8.3/§8.4's presentation rules: role and level chips, and the vocabulary
 * `AccessSourceBadges` renders a `pkg/access.Source` through. Everything here is cosmetic (§9.6)
 * — the four roles, the two levels and the three source kinds are the model itself
 * (`pkg/access`), never recomputed, only labelled.
 */

// ---------------------------------------------------------------------------------------------
// Roles (§8.2)
// ---------------------------------------------------------------------------------------------

/** `Role`'s colon-bearing values are not valid i18n path segments, so they are mapped, not interpolated. */
const roleKeys: Record<string, string> = {
    [Role.Admin]:             'admin',
    [Role.UserManager]:       'user_manager',
    [Role.AllInstancesRead]:  'all_instances_read',
    [Role.AllInstancesWrite]: 'all_instances_write',
}

/** A role's display name, or the raw value for one this build does not define (§10.4's "unknown denies"). */
export const roleLabel = (role: string): string => {
    const key = roleKeys[role]
    return key ? t(`users.roles.${key}.label`) : role
}

export const roleDescription = (role: string): string => {
    const key = roleKeys[role]
    return key ? t(`users.roles.${key}.description`) : ''
}

/**
 * The role chip (§9.1's "role chips"). `admin` and `user_manager` are administrative colours
 * (they are questions about the panel, not about any one instance); the two `all_instances:*`
 * roles take the same read/write colours `getAccessLevelTag` below uses, so a chip means the same
 * thing wherever it is drawn.
 */
export const getRoleTag = (role: string): TagItem => {
    const map: Record<string, TagItem> = {
        [Role.Admin]:             { label: roleLabel(Role.Admin),             color: 'primary' },
        [Role.UserManager]:       { label: roleLabel(Role.UserManager),       color: 'secondary' },
        [Role.AllInstancesWrite]: { label: roleLabel(Role.AllInstancesWrite), color: 'warning' },
        [Role.AllInstancesRead]:  { label: roleLabel(Role.AllInstancesRead),  color: 'info' },
    }

    return map[role] ?? { label: role, color: 'neutral' }
}

/** The four roles this build defines, in §8.2's own order — the roles editor's fixed row set. */
export const allRoles: string[] = [Role.Admin, Role.UserManager, Role.AllInstancesRead, Role.AllInstancesWrite]

// ---------------------------------------------------------------------------------------------
// Access levels (§8.3) and scoped-assignment option lists
// ---------------------------------------------------------------------------------------------

/**
 * The level chip. `write` is coloured as a thing worth a second look rather than as a plain
 * success, on purpose — this is who may change production configuration at a client site, and a
 * green badge reads as "nothing to see here" (§1: the panel is remote root on every client site).
 */
export const getAccessLevelTag = (level: string): TagItem => {
    const map: Record<string, TagItem> = {
        [AccessLevel.Write]: { label: t('access.level.write'), color: 'warning' },
        [AccessLevel.Read]:  { label: t('access.level.read'),  color: 'info' },
        [AccessLevel.None]:  { label: t('access.level.none'),  color: 'neutral' },
    }

    return map[level] ?? { label: level, color: 'neutral' }
}

export const assignmentLevelOptions = (): SelectOption[] => [
    { label: t('access.level.read'),  value: AccessLevel.Read },
    { label: t('access.level.write'), value: AccessLevel.Write },
]

export const assignmentScopeTypeOptions = (): SelectOption[] => [
    { label: t('access.scope.client'),   value: AccessScopeType.Client },
    { label: t('access.scope.instance'), value: AccessScopeType.Instance },
]

// ---------------------------------------------------------------------------------------------
// AccessSourceBadges (§9.3, §8.4)
// ---------------------------------------------------------------------------------------------

/**
 * One source, rendered as `{label, tooltip}`.
 *
 * `clientName`/`instanceName` are the names for *this screen's own context* — the row's client
 * for a `client_assignment` source, "this instance" for an `instance_assignment` one — never
 * resolved from `source.scope_id` here. Both transparency views already know which client or
 * instance is in context (`UserAccessRow` carries `client_name` per row; the instance access tab
 * has its own instance and client from `useInstanceContext`), which is the batched-lookup
 * `pkg/access.Source`'s own doc comment asks for: this function resolves nothing and does no N+1.
 */
export interface AccessSourceDisplay {
    label:   string
    tooltip: string
}

export const accessSourceDisplay = (
    source: IAccessSource,
    clientName: string,
    instanceName: string,
): AccessSourceDisplay => {
    switch (source.type) {
        case AccessSourceType.Role:
            return {
                label:   roleLabel(source.role),
                tooltip: t('access.source.role_tooltip', { role: roleLabel(source.role) }),
            }
        case AccessSourceType.ClientAssignment:
            return {
                label:   t('access.source.client_badge', { name: clientName || t('client.client') }),
                tooltip: t('access.source.client_tooltip', {
                    name:  clientName || t('client.client'),
                    level: t(`access.level.${source.level}`),
                }),
            }
        case AccessSourceType.InstanceAssignment:
            return {
                label:   t('access.source.instance_badge'),
                tooltip: t('access.source.instance_tooltip', {
                    name:  instanceName || t('instance.instance'),
                    level: t(`access.level.${source.level}`),
                }),
            }
        default:
            // §10.4: a source kind this build does not define is shown, never hidden — the "why"
            // screen must never look emptier than what enforcement actually granted.
            return { label: source.type || source.role, tooltip: '' }
    }
}
